import { apiClient } from "../client";
import type { Envelope, Product } from "./catalog";

export interface AddToCartPayload {
  product_id: number;
  quantity: number;
  variant_id?: number | null;
  /** Selected add-on option ids (product_variation_options.id) — priced on top of total_price. */
  variation_id?: number[];
}

export interface AddToCartResponse {
  status: boolean;
  message?: string;
}

export interface BuyNowPayload {
  product_id: number;
  quantity: number;
}

/** Upstream cart-line shape is loosely documented, so keep it permissive. */
export interface CartItem {
  id: number;
  product_id: number;
  quantity: number;
  variant_id?: number | null;
  product?: Product;
  price?: string | number;
  total?: string | number;
  [key: string]: unknown;
}

export interface CartTotals {
  subtotal?: string | number;
  discount?: string | number;
  tax?: string | number;
  total?: string | number;
  [key: string]: unknown;
}

export interface CartCount {
  count: number;
}

export interface ApplyCouponPayload {
  coupon_code: string;
  [key: string]: unknown;
}

/** Upstream coupon shape is loosely documented — keep permissive. */
export interface ApplyCouponResponse {
  status: boolean;
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

export interface UpdateCartQuantityPayload {
  cart_id: number;
  quantity: number;
}

export interface DeleteCartItemPayload {
  cart_id: number;
}

export const cart = {
  /** Add a product to the signed-in user's server-side cart. */
  addToCart: (payload: AddToCartPayload) =>
    apiClient.post<AddToCartResponse>("/cart/add-to-cart", payload),

  /** Skip the cart and go straight to checkout with a single product. */
  buyNow: (payload: BuyNowPayload) =>
    apiClient.post<AddToCartResponse>("/cart/buy-now", payload),

  /** Full cart listing for the current session/user. */
  get: () => apiClient.get<Envelope<CartItem[]> | CartItem[]>("/carts"),

  /** Cart line items only (lighter payload than `get`). */
  products: () => apiClient.get<Envelope<CartItem[]> | CartItem[]>("/cart/products"),

  /** Item count for the header badge. */
  count: () => apiClient.get<Envelope<CartCount> | CartCount>("/cart/count"),

  /** Subtotal/discount/total breakdown for the cart sidebar. */
  total: () => apiClient.get<Envelope<CartTotals> | CartTotals>("/cart/total"),

  /** Change the quantity of an existing cart line. */
  updateQuantity: (payload: UpdateCartQuantityPayload) =>
    apiClient.post<AddToCartResponse>("/update/cart/quantity", payload),

  /** Remove a line item from the cart. */
  removeItem: (payload: DeleteCartItemPayload) =>
    apiClient.post<AddToCartResponse>("/delete/cart/item", payload),

  /** Validate a coupon code against the current cart. */
  applyCoupon: (payload: ApplyCouponPayload) =>
    apiClient.post<ApplyCouponResponse>("/apply-coupon", payload),

  /** Remove a previously applied coupon from the cart. */
  removeCoupon: (payload: ApplyCouponPayload) =>
    apiClient.post<ApplyCouponResponse>("/remove-coupon", payload),
};
